/**
 * What the structure lock compares (PREC-4616): the steps the start reaches and the connections
 * between them. The builder cleans a stored graph when it loads it (`sanitizeEdges`: self-loops and
 * edges to missing nodes go), so the lock must read a graph the same way, or a locked campaign that
 * held such an edge could never be saved again. A step the start does not reach is not part of the
 * run, so adding or removing one is no change to it.
 */

interface LockNode {
	id: string;
	type?: string;
}

interface LockEdge {
	source: string;
	target: string;
}

interface LockGraph {
	initialNodes?: Array<Partial<LockNode> | null> | null;
	initialEdges?: Array<Partial<LockEdge> | null> | null;
}

const STARTER_NODE_TYPE = "starterNode";

/** The builder's edge clean-up: no self-loops, no edge to a node that does not exist. */
export function sanitizeCampaignEdges<E extends LockEdge>(
	edges: E[],
	nodes: { id: string }[]
): E[] {
	const nodeIds = new Set(nodes.map((node) => node.id));

	return edges.filter(
		(edge) => edge.source !== edge.target && nodeIds.has(edge.source) && nodeIds.has(edge.target)
	);
}

/**
 * The nodes the start reaches along the edges' direction, the start included. A graph without a
 * start is read as reaching every node, so the lock never goes blind on one.
 */
export function reachableCampaignNodeIds(nodes: LockNode[], edges: LockEdge[]): Set<string> {
	const starter = nodes.find((node) => node.type === STARTER_NODE_TYPE);

	if (!starter) {
		return new Set(nodes.map((node) => node.id));
	}

	const next = new Map<string, string[]>();

	for (const edge of sanitizeCampaignEdges(edges, nodes)) {
		next.set(edge.source, [...(next.get(edge.source) ?? []), edge.target]);
	}

	const reached = new Set([starter.id]);
	const queue = [starter.id];

	while (queue.length > 0) {
		for (const target of next.get(queue.shift() as string) ?? []) {
			if (!reached.has(target)) {
				reached.add(target);
				queue.push(target);
			}
		}
	}

	return reached;
}

/**
 * The run's steps (id and type) and the connections between them, deduplicated and sorted: equal
 * for two graphs exactly when the lock sees no structural change between them.
 */
export function campaignLockFingerprint(graph: LockGraph): string {
	const nodes = (graph.initialNodes ?? []).filter(
		(node): node is LockNode => typeof node?.id === "string"
	);
	const edges = (graph.initialEdges ?? []).filter(
		(edge): edge is LockEdge => typeof edge?.source === "string" && typeof edge?.target === "string"
	);
	const reached = reachableCampaignNodeIds(nodes, edges);
	const steps = nodes
		.filter((node) => reached.has(node.id))
		.map((node) => `${node.id}:${node.type}`)
		.sort();
	const connections = [
		...new Set(
			sanitizeCampaignEdges(edges, nodes)
				.filter((edge) => reached.has(edge.source) && reached.has(edge.target))
				.map((edge) => `${edge.source}>${edge.target}`)
		),
	].sort();

	return `${steps.join("|")}#${connections.join("|")}`;
}
